import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Mic, Square, AlertTriangle, Lock } from 'lucide-react';

const WINDOWS = [3, 5, 8, 10, 15];
const MAX_WPM = 260;
const TRIAL_SECONDS = 60;

// classify cadence -> {label, color}
const zoneFor = (wpm) => {
  if (wpm <= 0) return { label: 'Silent', color: '#5a5a64' };
  if (wpm < 120) return { label: 'Measured', color: '#5fb0e0' };
  if (wpm <= 180) return { label: 'Ideal Cadence', color: '#4ec98a' };
  if (wpm <= 220) return { label: 'Fast', color: '#e0b84c' };
  return { label: 'Too Fast', color: '#e05c5c' };
};

const PacerModal = ({ open, onClose, onPricing }) => {
  const [status, setStatus] = useState('idle'); // idle | running | denied | ended
  const [wpm, setWpm] = useState(0);
  const [peak, setPeak] = useState(0);
  const [windowSec, setWindowSec] = useState(5);
  const [timeLeft, setTimeLeft] = useState(TRIAL_SECONDS);

  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const recognitionRef = useRef(null);
  const wordTimesRef = useRef([]);
  const windowRef = useRef(windowSec);
  const trialTimerRef = useRef(null);
  const wpmRef = useRef(0);

  useEffect(() => { windowRef.current = windowSec; }, [windowSec]);

  const stopAll = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (trialTimerRef.current) clearInterval(trialTimerRef.current);
    try { recognitionRef.current && recognitionRef.current.stop(); } catch (e) {}
    recognitionRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  const reset = useCallback(() => {
    stopAll();
    setStatus('idle');
    setWpm(0);
    setPeak(0);
    setTimeLeft(TRIAL_SECONDS);
    wordTimesRef.current = [];
    wpmRef.current = 0;
  }, [stopAll]);

  useEffect(() => {
    if (!open) reset();
    return () => stopAll();
  }, [open, reset, stopAll]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const drawLoop = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (canvas && analyser) {
      const ctx = canvas.getContext('2d');
      const w = canvas.width;
      const h = canvas.height;
      const bufLen = analyser.frequencyBinCount;
      const data = new Uint8Array(bufLen);
      analyser.getByteFrequencyData(data);
      ctx.clearRect(0, 0, w, h);
      const bars = 48;
      const step = Math.floor(bufLen / bars);
      const barW = w / bars;
      for (let i = 0; i < bars; i++) {
        const v = data[i * step] / 255;
        const bh = Math.max(2, v * h * 0.9);
        const x = i * barW;
        const grad = ctx.createLinearGradient(0, h, 0, h - bh);
        grad.addColorStop(0, 'rgba(201,154,46,0.25)');
        grad.addColorStop(1, 'rgba(240,210,122,0.85)');
        ctx.fillStyle = grad;
        ctx.fillRect(x + barW * 0.2, h - bh, barW * 0.6, bh);
      }
    }

    // compute WPM from sliding word buffer
    const now = Date.now();
    const win = windowRef.current;
    wordTimesRef.current = wordTimesRef.current.filter((t) => now - t <= win * 1000);
    const count = wordTimesRef.current.length;
    const target = Math.min(MAX_WPM, Math.round((count / win) * 60));
    // smooth toward target
    wpmRef.current = wpmRef.current + (target - wpmRef.current) * 0.18;
    const shown = Math.round(wpmRef.current);
    setWpm(shown);
    setPeak((p) => (shown > p ? shown : p));

    rafRef.current = requestAnimationFrame(drawLoop);
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Speech recognition for real word counting
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-US';
        let lastInterimCount = 0;
        rec.onresult = (e) => {
          for (let i = e.resultIndex; i < e.results.length; i++) {
            const res = e.results[i];
            const words = res[0].transcript.trim().split(/\s+/).filter(Boolean);
            if (res.isFinal) {
              const t = Date.now();
              words.forEach(() => wordTimesRef.current.push(t));
              lastInterimCount = 0;
            } else {
              // account for new interim words for responsiveness
              const delta = words.length - lastInterimCount;
              if (delta > 0) {
                const t = Date.now();
                for (let k = 0; k < delta; k++) wordTimesRef.current.push(t);
                lastInterimCount = words.length;
              }
            }
          }
        };
        rec.onerror = () => {};
        rec.onend = () => {
          if (status === 'running' && recognitionRef.current) {
            try { rec.start(); } catch (e) {}
          }
        };
        recognitionRef.current = rec;
        try { rec.start(); } catch (e) {}
      }

      setStatus('running');
      setTimeLeft(TRIAL_SECONDS);
      wordTimesRef.current = [];
      wpmRef.current = 0;
      setPeak(0);
      rafRef.current = requestAnimationFrame(drawLoop);

      trialTimerRef.current = setInterval(() => {
        setTimeLeft((tl) => {
          if (tl <= 1) {
            clearInterval(trialTimerRef.current);
            stopAll();
            setStatus('ended');
            return 0;
          }
          return tl - 1;
        });
      }, 1000);
    } catch (err) {
      setStatus('denied');
    }
  }, [drawLoop, stopAll, status]);

  const stop = useCallback(() => {
    stopAll();
    setStatus('idle');
    setWpm(0);
  }, [stopAll]);

  if (!open) return null;

  const zone = zoneFor(wpm);
  const R = 120;
  const CIRC = 2 * Math.PI * R;
  const pct = Math.min(1, wpm / MAX_WPM);
  const dash = CIRC * pct;
  const mm = String(Math.floor(timeLeft / 60)).padStart(1, '0');
  const ss = String(timeLeft % 60).padStart(2, '0');

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="absolute inset-0 bg-[#08080c]" />
      <button onClick={onClose} className="fixed top-5 right-5 h-10 w-10 rounded-full bg-[#15151d] border border-[#2a2a33] flex items-center justify-center text-[#9a9aa4] hover:text-white transition-colors z-10">
        <X className="h-5 w-5" />
      </button>

      <div className="relative min-h-full flex flex-col items-center justify-center px-4 py-16">
        <div className="text-center mb-2">
          <h2 className="font-serif-display text-[30px] text-white">Live Cadence <span className="gold-gradient-text">Pacer</span></h2>
          <p className="text-[13px] text-[#8a8a94] mt-1">Real-time visual WPM diagnostics — all processing stays in your browser.</p>
        </div>

        {/* Trial badge */}
        {status !== 'ended' && (
          <div className="mt-3 mb-6 px-4 py-1.5 rounded-full bg-[#0e0e15] border border-[#22222b] text-[12px] text-[#9a9aa4] flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: status === 'running' ? '#4ec98a' : '#5a5a64' }} />
            Trial Preview · {mm}:{ss} remaining
          </div>
        )}

        {status === 'ended' ? (
          <div className="mt-4 w-full max-w-md rounded-2xl bg-[#0e0e15] border border-[#c99a2e]/40 gold-glow p-8 text-center animate-fade-up">
            <Lock className="h-8 w-8 text-[#c99a2e] mx-auto" />
            <h3 className="font-serif-display text-[24px] text-white mt-4">Your 1-minute trial ended</h3>
            <p className="text-[13.5px] text-[#9a9aa4] mt-2">Peak reading this session: <span className="text-[#e0b84c] font-semibold">{peak} WPM</span></p>
            <p className="text-[13.5px] text-[#9a9aa4] mt-1">Unlock unlimited pacing sessions and advanced metrics with PACE MASTER Premium.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={onPricing} className="px-6 py-2.5 rounded-lg bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500] font-semibold hover:from-[#f0d27a] hover:to-[#e0b84c] transition-all">View Pricing</button>
              <button onClick={reset} className="px-6 py-2.5 rounded-lg bg-transparent border border-[#3a3a45] text-white font-semibold hover:border-[#c99a2e]/50 transition-all">Restart Trial</button>
            </div>
          </div>
        ) : (
          <>
            {/* Gauge */}
            <div className="relative" style={{ width: 300, height: 300 }}>
              <svg width="300" height="300" className="-rotate-90">
                <circle cx="150" cy="150" r={R} fill="none" stroke="#1a1a22" strokeWidth="16" />
                <circle
                  cx="150" cy="150" r={R} fill="none" stroke={zone.color} strokeWidth="16" strokeLinecap="round"
                  strokeDasharray={`${dash} ${CIRC}`}
                  style={{ transition: 'stroke 0.4s ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-[64px] font-bold text-white leading-none tabular-nums">{wpm}</div>
                <div className="text-[13px] tracking-[0.25em] text-[#8a8a94] mt-1">WPM</div>
                <div className="mt-2 text-[13px] font-semibold" style={{ color: zone.color }}>{zone.label}</div>
              </div>
            </div>

            {/* Audio waves */}
            <div className="mt-6 w-full max-w-md rounded-xl bg-[#0b0b11] border border-[#1c1c24] p-3">
              <canvas ref={canvasRef} width={440} height={70} className="w-full h-[70px]" />
            </div>

            {/* Peak + window */}
            <div className="mt-5 flex flex-col items-center gap-4">
              <div className="text-[12.5px] text-[#8a8a94]">Session Peak: <span className="text-[#e0b84c] font-semibold">{peak} WPM</span></div>

              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[#8a8a94] mr-1">Averaging window:</span>
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    onClick={() => setWindowSec(w)}
                    className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${
                      windowSec === w
                        ? 'bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500]'
                        : 'bg-[#121218] text-[#9a9aa4] border border-[#2a2a33] hover:border-[#c99a2e]/40'
                    }`}
                  >
                    {w}s
                  </button>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div className="mt-7">
              {status === 'idle' && (
                <button onClick={start} className="flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500] font-semibold hover:from-[#f0d27a] hover:to-[#e0b84c] transition-all shadow-[0_8px_28px_-8px_rgba(212,175,55,0.6)]">
                  <Mic className="h-5 w-5" /> Enable Microphone & Start
                </button>
              )}
              {status === 'running' && (
                <button onClick={stop} className="flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#121218] text-white font-semibold border border-[#2a2a33] hover:border-[#e05c5c]/60 transition-all">
                  <Square className="h-4 w-4" /> Stop Session
                </button>
              )}
              {status === 'denied' && (
                <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1a1010] border border-[#e05c5c]/40 text-[#e08c8c] text-[13px]">
                  <AlertTriangle className="h-4 w-4" /> Microphone access denied. Please allow mic permission and try again.
                </div>
              )}
            </div>

            {status === 'idle' && (
              <p className="mt-4 text-[11.5px] text-[#5a5a64] max-w-sm text-center">
                Uses your browser's speech engine to visualize speaking cadence. Nothing is recorded or transmitted.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PacerModal;
